import { useTranslation } from 'react-i18next';
/**
 * Generic "Showing X-Y of N · Prev | Next" footer for paginated tables.
 *
 * Pure presentational — parent owns page/pageSize state and the fetch
 * loop. We just render the controls and call back into the parent.
 *
 * Props:
 *   total       — total number of rows on the server (from X-Total-Count)
 *   skip        — current zero-indexed offset
 *   limit       — current page size
 *   onChange({skip, limit}) — invoked when user clicks Prev/Next or
 *                              changes the page-size dropdown
 *   testIdPrefix — optional prefix for the generated data-testids
 */
import React from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export default function PaginationFooter({
  total, skip, limit, onChange, testIdPrefix = 'pagination',
}) {
  const { t } = useTranslation();
  const safeTotal = Math.max(0, total || 0);
  const start = safeTotal === 0 ? 0 : skip + 1;
  const end = Math.min(skip + limit, safeTotal);
  const hasPrev = skip > 0;
  const hasNext = end < safeTotal;
  const goFirst = () => onChange({ skip: 0, limit });
  const goPrev = () => onChange({ skip: Math.max(0, skip - limit), limit });
  const goNext = () => onChange({ skip: skip + limit, limit });
  const goLast = () => {
    const lastSkip = Math.max(0, (Math.ceil(safeTotal / limit) - 1) * limit);
    onChange({ skip: lastSkip, limit });
  };

  return (
    <div
      className="flex items-center justify-between gap-4 pt-3 mt-2 border-t text-xs text-slate-600"
      data-testid={testIdPrefix}
    >
      <div data-testid={`${testIdPrefix}-summary`}>
        {safeTotal === 0
          ? 'No results'
          : <>{t('auto.misc2.showing')} <span className="font-medium">{start}</span>–<span className="font-medium">{end}</span> of <span className="font-medium">{safeTotal}</span></>}
      </div>

      <div className="flex items-center gap-3">
        {/* Page-size selector — using native <select> to keep payload
            light and avoid focus-trap quirks of the Shadcn Select inside
            scrollable card. */}
        <label className="flex items-center gap-1.5">
          <span className="text-slate-500">{t('auto.misc2.rowsPage')}</span>
          <select
            value={limit}
            onChange={(e) => onChange({ skip: 0, limit: parseInt(e.target.value, 10) })}
            className="h-7 rounded border border-slate-300 bg-white px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            data-testid={`${testIdPrefix}-page-size`}
          >
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>

        <div className="flex items-center gap-0.5">
          <Button size="icon" variant="ghost" className="h-7 w-7"
            onClick={goFirst} disabled={!hasPrev}
            title="First page" data-testid={`${testIdPrefix}-first`}>
            <ChevronsLeft className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7"
            onClick={goPrev} disabled={!hasPrev}
            title="Previous page" data-testid={`${testIdPrefix}-prev`}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7"
            onClick={goNext} disabled={!hasNext}
            title="Next page" data-testid={`${testIdPrefix}-next`}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7"
            onClick={goLast} disabled={!hasNext}
            title="Last page" data-testid={`${testIdPrefix}-last`}>
            <ChevronsRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
